import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Alert,
  Anchor,
  Divider,
  Checkbox,
} from '@mantine/core';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import { useAuth } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';

/**
 * Login page — mirrors ccms_v2's flow.
 *
 * If the backend reports EMAIL_NOT_CONFIRMED, redirect to the confirm-email
 * page so the user can resend the link instead of being stuck.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useEnrichedAuth();
  const { planningCentre } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startingPco, setStartingPco] = useState(false);

  const redirectTo = searchParams.get('redirect') || '/';

  const onPlanningCentreSignIn = async () => {
    setStartingPco(true);
    setError(null);
    try {
      // Tell the backend to come back to *this* app's host. The backend validates
      // it against PlanningCentreLogin:AllowedRedirectUris before forwarding to PCO.
      const redirectUri = `${window.location.origin}/auth/oauth/planning-centre/callback`;
      const { authUrl } = await planningCentre.start(redirectTo, redirectUri);
      window.location.assign(authUrl);
    } catch (err: any) {
      setError(err?.message || 'Could not start Planning Centre sign-in');
      setStartingPco(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.login(email.trim(), password, rememberMe);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const msg = err?.message || 'Login failed';
      // Backend convention: EMAIL_NOT_CONFIRMED prefix
      if (msg.includes('EMAIL_NOT_CONFIRMED')) {
        navigate(`/auth/confirm-email?email=${encodeURIComponent(email.trim())}`, { replace: true });
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size={420} my={60}>
      <Title order={2} ta="center" mb="md">
        Welcome back
      </Title>
      <Text c="dimmed" size="sm" ta="center" mb="xl">
        Sign in to access your courses and personalised content.
      </Text>

      <Paper withBorder shadow="sm" p="lg" radius="md">
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}
            <TextInput
              label="Email"
              type="email"
              placeholder="you@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoComplete="email"
            />
            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="current-password"
            />
            <Group justify="space-between">
              <Checkbox
                label="Remember me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.currentTarget.checked)}
              />
              <Anchor component={Link} to="/auth/forgot-password" size="sm">
                Forgot password?
              </Anchor>
            </Group>
            <Button type="submit" loading={submitting} leftSection={<IconLock size={16} />} fullWidth>
              Sign in
            </Button>
          </Stack>
        </form>
        <Divider label="OR" labelPosition="center" my="lg" />
        <Button
          type="button"
          variant="default"
          fullWidth
          loading={startingPco}
          onClick={onPlanningCentreSignIn}
        >
          Continue with Planning Centre
        </Button>
        <Divider my="lg" />
        <Text size="sm" ta="center">
          Don't have an account?{' '}
          <Anchor component={Link} to="/auth/register" fw={600}>
            Create one
          </Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
